import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { API } from "@/lib/api";
import { BookOpen, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function PreviewDialog({ open, onOpenChange, bookId, bookTitle }) {
  const previewUrl = `${API}/preview/${bookId}`;
  const { user } = useAuth();

  // Track preview event once when opened, only for signed-in users.
  // Powers the abandoned-preview nudge (10% off after 7 days if not purchased).
  useEffect(() => {
    if (!open || !user || !bookId) return;
    const form = new FormData();
    form.append("book_id", bookId);
    api.post("/previews/track", form).catch(() => {}); // silent
  }, [open, user, bookId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="preview-dialog"
        className="max-w-4xl w-[92vw] h-[85vh] p-0 bg-paper rounded-none border border-edge flex flex-col"
      >
        <DialogHeader className="px-6 py-4 border-b border-edge flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3 min-w-0">
            <BookOpen className="w-4 h-4 text-terracotta flex-shrink-0" strokeWidth={1.5} />
            <DialogTitle className="font-serif text-xl truncate">
              First 3 pages — <span className="italic">{bookTitle}</span>
            </DialogTitle>
          </div>
          <a
            href={previewUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="preview-open-new-tab"
            className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-muted_ink hover:text-terracotta"
          >
            Open <ExternalLink className="w-3 h-3" />
          </a>
        </DialogHeader>
        <div className="flex-1 bg-linen overflow-hidden">
          <iframe
            data-testid="preview-iframe"
            src={previewUrl}
            title={`${bookTitle} preview`}
            className="w-full h-full border-0"
          />
        </div>
        <div className="px-6 py-3 border-t border-edge label-eyebrow text-muted_ink text-center">
          Like what you're reading? Purchase to unlock the full book.
        </div>
      </DialogContent>
    </Dialog>
  );
}
